<template>
  <v-dialog
    v-bind="{value}"
    max-width="800px"
    persistent
  >
    <key-refresher
      ref="serverListChangeDialog"
      unique-key="server-list-change-dialog"
    >
      <template v-slot="{ refreshKey }">
        <confirm-server-list-change-dialog
          :key="refreshKey"
          v-model="showConfirmServerListChange"
          :verbiage="['Add', 'to']"
          :server-ids="selectedServers.map(s => s.id)"
          :reservation-id="reservationId"
          :date="date"
          @saved="() => {
            $emit('saved')
            close()
          }"
          @close="() => {
            const ref = $refs.serverListChangeDialog
            if (ref) ref.refresh(200)
          }"
        />
      </template>
    </key-refresher>
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            icon
            class="mr-2"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          Add Servers
          <v-spacer />
          <v-btn
            :light="isDirty"
            :disabled="!isDirty"
            :color="isDirty ? 'green' : 'primary darken-1'"
            @click="showConfirmServerListChange = true"
          >
            Save
          </v-btn>
        </v-toolbar>
      </v-card-title>
      <api-errors :errors="errors" />
      <loading-bar v-if="isLoading" />
      <div
        v-else-if="!servers.length"
        class="pa-2"
      >
        <v-card class="ma-2 pa-2 text-center font-weight-bold">
          No Servers Available in Meal Period...
        </v-card>
      </div>
      <v-row
        v-else
        no-gutters
        class="justify-center pb-2"
      >
        <v-col
          v-for="s of servers"
          :key="`server-${s.id}`"
          cols="12"
          sm="6"
          md="4"
          class="px-3 pt-3"
        >
          <v-card
            class="d-flex justify-center pa-2"
            @click="selectServer(s)"
          >
            <v-simple-checkbox
              :value="!!selectedServersMap[s.id]"
              :ripple="false"
              :color="s.color"
              @click.stop="selectServer(s)"
            />
            <div
              :class="{'text-decoration-line-through': !s.active}"
              class="d-flex align-self-center font-weight-bold"
            >
              {{ s.name }}
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import {apiHelpers, errorHandler} from '@/ft-vue-core'
import KeyRefresher from '@/ft-vue-core/components/helpers/KeyRefresher'
import LoadingBar from '../helperComponents/LoadingBar'
import ConfirmServerListChangeDialog from './ConfirmServerListChangeDialog.vue'
export default {
  name: 'AddServersDialog',
  components: {LoadingBar, ConfirmServerListChangeDialog, KeyRefresher},
  mixins: [apiHelpers, errorHandler],
  props: {
    value: Boolean,
    /** Selected date pass-through */
    date: {
      type: String,
      required: true
    },
    /** Array of `DiningReservationTableAssignment` */
    tables: {
      type: Array,
      required: true
    },
    /**
     * Reservation ID pass-through for `save`
     */
    reservationId: {
      type: Number,
      required: true
    },
    /** Meal-Period ID pass-through */
    mealPeriodId: Number,
    forEvent: Boolean,
    eventId: Number,
  },
  data () {
    return {
      /** Queried list of servers */
      servers: [],
      /**
       * Servers selected to be added to this reservation/table
       * @see selectedServersMap
       */
      selectedServers: [],
      /** Controller for showing confirmation dialog */
      showConfirmServerListChange: false,
    }
  },
  computed: {
    /** Control availability of the `save` button based on the length of selectedServers */
    isDirty () {
      return !!this.selectedServers?.length
    },
    /**
     * `selectedServers` mapped with index included to anticipate splicing
     * @see selectedServers
     */
    selectedServersMap () {
      return this.selectedServers?.reduce((acc, r, i) => {
        acc[r.id] = Object.assign(r, {_index: i})
        return acc
      }, {}) ?? {}
    }
  },
  watch: {
    /**
     * When dialog opens, query a list of servers that can be added
     *
     * When dialog closes, wait for animation to complete and trigger
     * a key-refresh of this component
     * @param {Boolean} newVal open/close status of dialog
     */
    value (newVal) {
      if (!newVal) setTimeout(() => this.$emit('close'), 200) // On close key-refresh this component
      else {
        this.$ftAPI().get(this.addResourceErrorHandler({
          command: 'diningServer',
          loading: this.setIsLoading,
          load: this.servers,
          params: {
            filter_methods: this.forEvent
              ? {
                filterOutByEventAssignment: [this.eventId, this.reservationId, this.tables.map(t => t.locationTableId) ?? []],
                filterByInEvent: [this.eventId]
              }
              : {
                filterOutByAssignment: [this.date?.substring(0, 10), this.reservationId, this.tables?.map(t => t.locationTableId) ?? []],
                filterByMealPeriodDate: [this.mealPeriodId, this.date]
              }
          }
        }))
      }
    }
  },
  methods: {
    close () {this.$emit('input', false)},
    /**
     * Add/Remove Server from selectedServers
     * @see selectedServers
     */
    selectServer (server) {
      const {id: serverId} = server
      if (!this.selectedServersMap[serverId]) this.selectedServers.push(server)
      else this.selectedServers.splice(this.selectedServersMap[serverId]._index, 1)
    },
  }
}
</script>
